/**
 * Service for searching literary features across all passages and books
 *
 * Performance Optimizations:
 * - Search term normalization done once per search (not per comparison)
 * - Map-based lookups for O(1) passage retrieval
 * - Early exits and minimal string operations
 * - Optional search index creation for repeated searches (see createSearchIndex)
 *
 * Performance Characteristics:
 * - searchLiteraryDevices: O(n*m) where n=passages, m=avg devices per passage
 * - searchThemes: O(n*m) where n=passages, m=avg themes per passage
 * - groupResultsByBook: O(n) where n=results
 * - createSearchIndex: O(n*m) one-time cost, enables O(1) lookups for future searches
 *
 * Typical dataset size: ~1,000 passages across 66 books
 */

export interface CrossReferenceResult {
  passageId: string;
  book: string;
  ref: string;
  title: string;
  context?: string; // Brief snippet showing how the feature is used
  source: 'passage' | 'book';
}

/**
 * Search for a specific literary device across all passages
 * Performance optimizations:
 * - Normalize search term once instead of on every comparison
 * - Use Map for O(1) passage lookups
 * - Minimize string operations
 */
export function searchLiteraryDevices(
  deviceName: string,
  allPassageFeatures: LiteraryFeaturesByPassage[],
  allPassages: Passage[]
): CrossReferenceResult[] {
  const results: CrossReferenceResult[] = [];

  // Normalize search term once
  const normalizedDeviceName = deviceName.toLowerCase();

  // Create a map of passageId to passage for O(1) lookup
  const passageMap = new Map(allPassages.map(p => [p.id, p]));

  for (const passageFeature of allPassageFeatures) {
    const devices = passageFeature.literaryDevices;
    if (!devices?.length) continue;

    // Find all instances of this device in this passage
    const matchingDevices = devices.filter(
      device => device.device.toLowerCase() === normalizedDeviceName
    );

    if (matchingDevices.length === 0) continue;

    const passage = passageMap.get(passageFeature.passageId);
    if (!passage) continue;

    // Build context string efficiently
    let context = '';
    let count = 0;
    for (const device of matchingDevices) {
      if (count >= 2) break; // Limit to first 2 examples
      const text = device.text || device.explanation;
      if (text) {
        context += (count > 0 ? ' ... ' : '') + text;
        count++;
      }
    }

    results.push({
      passageId: passageFeature.passageId,
      book: passage.book,
      ref: passage.ref,
      title: passage.title,
      context,
      source: 'passage'
    });
  }

  return results;
}


/**
 * Search for a specific theme across all passages
 * Performance optimizations:
 * - Normalize search term once instead of on every comparison
 * - Use early exit when theme is found
 */
export function searchThemes(
  themeName: string,
  allPassages: Passage[]
): CrossReferenceResult[] {
  const results: CrossReferenceResult[] = [];

  // Normalize search term once
  const normalizedThemeName = themeName.toLowerCase();

  for (const passage of allPassages) {
    const themes = passage.themes;
    if (!themes?.length) continue;

    // Check if any theme matches (case-insensitive)
    let hasTheme = false;
    for (const theme of themes) {
      if (theme.toLowerCase() === normalizedThemeName) {
        hasTheme = true;
        break; // Early exit once found
      }
    }

    if (hasTheme) {
      results.push({
        passageId: passage.id,
        book: passage.book,
        ref: passage.ref,
        title: passage.title,
        context: passage.summary,
        source: 'passage'
      });
    }
  }

  return results;
}

/**
 * Search for a specific subgenre across all passages
 */
export function searchSubgenre(
  subgenreName: string,
  allPassages: Passage[]
): CrossReferenceResult[] {
  const results: CrossReferenceResult[] = [];
  const normalizedSubgenreName = subgenreName.toLowerCase();

  for (const passage of allPassages) {
    const subgenre = passage.subgenre;
    if (!subgenre) continue;

    if (subgenre.toLowerCase() === normalizedSubgenreName) {
      results.push({
        passageId: passage.id,
        book: passage.book,
        ref: passage.ref,
        title: passage.title,
        context: passage.summary,
        source: 'passage'
      });
    }
  }

  return results;
}

/**
 * Search for a specific top-level genre across all passages
 */
export function searchGenre(
  genreName: string,
  allPassages: Passage[]
): CrossReferenceResult[] {
  const results: CrossReferenceResult[] = [];
  const normalizedGenreName = genreName.toLowerCase();

  for (const passage of allPassages) {
    const genre = passage.genre;
    if (!genre) continue;

    if (genre.toLowerCase() === normalizedGenreName) {
      results.push({
        passageId: passage.id,
        book: passage.book,
        ref: passage.ref,
        title: passage.title,
        context: passage.summary,
        source: 'passage'
      });
    }
  }

  return results;
}

/**
 * Search for a specific literary form across all passages
 */
export function searchForm(
  formName: string,
  allPassages: Passage[]
): CrossReferenceResult[] {
  const results: CrossReferenceResult[] = [];
  const normalizedFormName = formName.toLowerCase();

  for (const passage of allPassages) {
    const form = passage.form;
    if (!form) continue;

    if (form.toLowerCase() === normalizedFormName) {
      results.push({
        passageId: passage.id,
        book: passage.book,
        ref: passage.ref,
        title: passage.title,
        context: passage.summary,
        source: 'passage'
      });
    }
  }

  return results;
}

/**
 * Search for a specific poetic feature across all passages
 */
export function searchPoeticFeatures(
  featureName: string,
  allPassageFeatures: LiteraryFeaturesByPassage[],
  allPassages: Passage[]
): CrossReferenceResult[] {
  const results: CrossReferenceResult[] = [];
  const normalizedFeatureName = featureName.toLowerCase();
  const passageMap = new Map(allPassages.map(p => [p.id, p]));

  for (const passageFeature of allPassageFeatures) {
    const features = passageFeature.poeticFeatures;
    if (!features?.length) continue;

    const matchingFeatures = features.filter(
      feature => feature.feature.toLowerCase() === normalizedFeatureName
    );

    if (matchingFeatures.length === 0) continue;

    const passage = passageMap.get(passageFeature.passageId);
    if (!passage) continue;

    let context = '';
    let count = 0;
    for (const feature of matchingFeatures) {
      if (count >= 2) break;
      const text = feature.description;
      if (text) {
        context += (count > 0 ? ' ... ' : '') + text;
        count++;
      }
    }

    results.push({
      passageId: passageFeature.passageId,
      book: passage.book,
      ref: passage.ref,
      title: passage.title,
      context,
      source: 'passage'
    });
  }

  return results;
}

/**
 * Search for a specific imagery/symbol across all passages
 */
export function searchImagery(
  imageName: string,
  allPassageFeatures: LiteraryFeaturesByPassage[],
  allPassages: Passage[]
): CrossReferenceResult[] {
  const results: CrossReferenceResult[] = [];
  const normalizedImageName = imageName.toLowerCase();
  const passageMap = new Map(allPassages.map(p => [p.id, p]));

  for (const passageFeature of allPassageFeatures) {
    const imagery = passageFeature.imagery;
    if (!imagery?.length) continue;

    const matchingImagery = imagery.filter(
      img => img.image.toLowerCase() === normalizedImageName
    );

    if (matchingImagery.length === 0) continue;

    const passage = passageMap.get(passageFeature.passageId);
    if (!passage) continue;

    let context = '';
    let count = 0;
    for (const img of matchingImagery) {
      if (count >= 2) break;
      const text = img.meaning;
      if (text) {
        context += (count > 0 ? ' ... ' : '') + text;
        count++;
      }
    }

    results.push({
      passageId: passageFeature.passageId,
      book: passage.book,
      ref: passage.ref,
      title: passage.title,
      context,
      source: 'passage'
    });
  }

  return results;
}

/**
 * Search for a specific narrative technique across all passages
 */
export function searchNarrativeTechniques(
  techniqueName: string,
  allPassageFeatures: LiteraryFeaturesByPassage[],
  allPassages: Passage[]
): CrossReferenceResult[] {
  const results: CrossReferenceResult[] = [];
  const normalizedTechniqueName = techniqueName.toLowerCase();
  const passageMap = new Map(allPassages.map(p => [p.id, p]));

  for (const passageFeature of allPassageFeatures) {
    const techniques = passageFeature.narrativeTechniques;
    if (!techniques?.length) continue;

    const matchingTechniques = techniques.filter(
      tech => tech.technique.toLowerCase() === normalizedTechniqueName
    );

    if (matchingTechniques.length === 0) continue;

    const passage = passageMap.get(passageFeature.passageId);
    if (!passage) continue;

    let context = '';
    let count = 0;
    for (const tech of matchingTechniques) {
      if (count >= 2) break;
      const text = tech.description;
      if (text) {
        context += (count > 0 ? ' ... ' : '') + text;
        count++;
      }
    }

    results.push({
      passageId: passageFeature.passageId,
      book: passage.book,
      ref: passage.ref,
      title: passage.title,
      context,
      source: 'passage'
    });
  }

  return results;
}

/**
 * Search for a specific micro structure type across all passages
 */
export function searchMicroStructures(
  structureType: string,
  allPassageFeatures: LiteraryFeaturesByPassage[],
  allPassages: Passage[]
): CrossReferenceResult[] {
  const results: CrossReferenceResult[] = [];
  const normalizedStructureType = structureType.toLowerCase();
  const passageMap = new Map(allPassages.map(p => [p.id, p]));

  for (const passageFeature of allPassageFeatures) {
    const structures = passageFeature.microStructures;
    if (!structures?.length) continue;

    const matchingStructures = structures.filter(
      struct => struct.type.toLowerCase() === normalizedStructureType
    );

    if (matchingStructures.length === 0) continue;

    const passage = passageMap.get(passageFeature.passageId);
    if (!passage) continue;

    let context = '';
    let count = 0;
    for (const struct of matchingStructures) {
      if (count >= 2) break;
      const text = struct.pattern;
      if (text) {
        context += (count > 0 ? ' ... ' : '') + text;
        count++;
      }
    }

    results.push({
      passageId: passageFeature.passageId,
      book: passage.book,
      ref: passage.ref,
      title: passage.title,
      context,
      source: 'passage'
    });
  }

  return results;
}


/**
 * Search for a specific macro structure type across all books
 */
export function searchMacroStructures(
  structureType: string,
  allBookFeatures: LiteraryFeaturesByBook[],
  allPassages: Passage[]
): CrossReferenceResult[] {
  const results: CrossReferenceResult[] = [];
  const normalizedStructureType = structureType.toLowerCase();

  // Group passages by book for quick lookup
  const passagesByBook = new Map<string, Passage[]>();
  for (const passage of allPassages) {
    if (!passagesByBook.has(passage.book)) {
      passagesByBook.set(passage.book, []);
    }
    passagesByBook.get(passage.book)!.push(passage);
  }

  for (const bookFeature of allBookFeatures) {
    const structures = bookFeature.macroStructure;
    if (!structures?.length) continue;

    const matchingStructures = structures.filter(
      struct => struct.type.toLowerCase() === normalizedStructureType
    );

    if (matchingStructures.length === 0) continue;

    // Get all passages from this book
    const bookPassages = passagesByBook.get(bookFeature.book) || [];

    if (bookPassages.length > 0) {
      const firstPassage = bookPassages[0];
      const structure = matchingStructures[0];
      const context = structure.description;

      results.push({
        passageId: firstPassage.id,
        book: bookFeature.book,
        ref: bookFeature.book,
        title: `${bookFeature.book} (Book-level)`,
        context,
        source: 'book'
      });
    }
  }

  return results;
}

/**
 * Group results by book
 * Performance: O(n) single pass through results
 */
export function groupResultsByBook(results: CrossReferenceResult[]): Record<string, CrossReferenceResult[]> {
  const grouped: Record<string, CrossReferenceResult[]> = {};

  for (const result of results) {
    const book = result.book;
    if (!grouped[book]) {
      grouped[book] = [];
    }
    grouped[book].push(result);
  }

  return grouped;
}

/**
 * Create a search index for faster lookups
 * This can be called once when data is loaded and reused for multiple searches
 *
 * @returns An object with indexed data for faster searching
 */
export function createSearchIndex(
  allPassageFeatures: LiteraryFeaturesByPassage[],
  allPassages: Passage[]
) {
  // Create passage lookup map
  const passageMap = new Map(allPassages.map(p => [p.id, p]));

  // Index literary devices by normalized name
  const deviceIndex = new Map<string, Set<string>>(); // deviceName -> Set of passageIds

  // Index rhetorical features by normalized name
  const featureIndex = new Map<string, Set<string>>(); // featureName -> Set of passageIds

  // Index themes by normalized name
  const themeIndex = new Map<string, Set<string>>(); // themeName -> Set of passageIds

  // Build device and feature indices
  for (const passageFeature of allPassageFeatures) {
    const passageId = passageFeature.passageId;

    // Index literary devices
    if (passageFeature.literaryDevices?.length) {
      for (const device of passageFeature.literaryDevices) {
        const normalizedName = device.device.toLowerCase();
        if (!deviceIndex.has(normalizedName)) {
          deviceIndex.set(normalizedName, new Set());
        }
        deviceIndex.get(normalizedName)!.add(passageId);
      }
    }

    // Index literary devices
    if (passageFeature.literaryDevices?.length) {
      for (const device of passageFeature.literaryDevices) {
        const normalizedName = device.device.toLowerCase();
        if (!featureIndex.has(normalizedName)) {
          featureIndex.set(normalizedName, new Set());
        }
        featureIndex.get(normalizedName)!.add(passageId);
      }
    }
  }

  // Build theme index
  for (const passage of allPassages) {
    if (passage.themes?.length) {
      for (const theme of passage.themes) {
        const normalizedName = theme.toLowerCase();
        if (!themeIndex.has(normalizedName)) {
          themeIndex.set(normalizedName, new Set());
        }
        themeIndex.get(normalizedName)!.add(passage.id);
      }
    }
  }

  return {
    passageMap,
    deviceIndex,
    featureIndex,
    themeIndex
  };
}

export type SearchIndex = ReturnType<typeof createSearchIndex>;

